import { countBy, sortBy } from 'utils/array';

// ============================================
// CSS Variable Resolution
// ============================================

export function getCSSColor(cssVar: string, fallback: string): string {
    const value = getComputedStyle(document.documentElement)
        .getPropertyValue(cssVar)
        .trim();
    return value || fallback;
}

function resolveColorGroup<K extends string>(
    cssVars: Record<K, string>,
    fallbacks: Record<K, string>,
): Record<K, string> {
    const result = {} as Record<K, string>;
    for (const key of Object.keys(fallbacks) as K[]) {
        result[key] = getCSSColor(cssVars[key], fallbacks[key]);
    }
    return result;
}

// ============================================
// Color Groups
//
// Each group pairs CSS variable names with a fallback enum.
// Enum values are light-theme defaults used when CSS
// variables cannot be resolved at runtime.
// ============================================

// --- Trend Line Colors ---

export const TREND_LINE_CSS_VARS = [
    '--trend-indicators-1',
    '--trend-indicators-2',
    '--trend-indicators-3',
    '--trend-indicators-4',
    '--trend-indicators-5',
    '--trend-indicators-6',
    '--trend-indicators-7',
    '--trend-indicators-8',
    '--trend-indicators-9',
    '--trend-indicators-10',
    '--trend-indicators-11',
    '--trend-indicators-12',
    '--trend-indicators-13',
    '--trend-indicators-14',
    '--trend-indicators-15',
    '--trend-indicators-16',
    '--trend-indicators-17',
    '--trend-indicators-18',
    '--trend-indicators-19',
    '--trend-indicators-20',
] as const;

/**
 * 20 distinct colors to differentiate multiple data series.
 * Note: this list _must_ match the values in TREND_LINE_CSS_VARS,
 * or it will not pass workbench state serialization validation.
 */
export enum TrendLineColors {
    trendLine1 = '#0070c4', // bluecta-600 (rebrand)
    trendLine2 = '#ffc107',
    trendLine3 = '#d10d54',
    trendLine4 = '#6f3fc9',
    trendLine5 = '#1de9b6',
    trendLine6 = '#c750df',
    trendLine7 = '#009ce1',
    trendLine8 = '#48a748',
    trendLine9 = '#1740d9',
    trendLine10 = '#f29d01',
    trendLine11 = '#ceb900',
    trendLine12 = '#9c27b0',
    trendLine13 = '#00bcd4',
    trendLine14 = '#c2d035',
    trendLine15 = '#fe5918',
    trendLine16 = '#52def0',
    trendLine17 = '#ff80ab',
    trendLine18 = '#8bc34a',
    trendLine19 = '#ea80fc',
    trendLine20 = '#006f7d',
}

export const TrendLineColorsList = Object.values(TrendLineColors);

export function getTrendLineColorsFromCSS(): string[] {
    return TREND_LINE_CSS_VARS.map((cssVar, index) =>
        getCSSColor(cssVar, TrendLineColorsList[index]),
    );
}

// --- Health Range Colors ---

export const HEALTH_RANGE_CSS_VARS = {
    unspecified: '--health-range-unspecified',
    off: '--health-range-off',
    healthy: '--health-range-healthy',
    faultDeveloping: '--health-range-fault-developing',
    unhealthy: '--health-range-unhealthy',
} as const;

export enum HealthRangeColors {
    unspecified = '#a2a7b3', // grey-400
    off = '#273D5A', // blueGrey
    healthy = '#228910', // green-800 (rebrand)
    faultDeveloping = '#e99c3d', // orange-600 (rebrand)
    unhealthy = '#e12635', // red-700 (rebrand)
}

export function getHealthRangeColorsFromCSS() {
    return resolveColorGroup(HEALTH_RANGE_CSS_VARS, HealthRangeColors);
}

// --- Activity Line Colors ---

export const ACTIVITY_LINE_CSS_VARS = {
    Alarm: '--activity-line-alarm',
    Warning: '--activity-line-warning',
    Manual: '--activity-line-manual',
    Automatic: '--activity-line-automatic',
    MachineIQ: '--activity-line-machineiq',
    MultipleActivities: '--activity-line-multiple',
    CreationMarker: '--activity-line-creation-marker',
    CriticalAlarm: '--activity-line-critical-alarm',
} as const;

export enum ActivityLineColors {
    Alarm = '#ff1537',
    Warning = '#f29d01',
    Manual = '#0f48cd',
    Automatic = '#6c7c97',
    MachineIQ = '#39116d',
    MultipleActivities = '#0193a0',
    CreationMarker = '#2d3c51',
    CriticalAlarm = '#d2001b',
}

export function getActivityLineColorsFromCSS() {
    return resolveColorGroup(ACTIVITY_LINE_CSS_VARS, ActivityLineColors);
}

// --- Analysis Marker Colors ---
export const ANALYSIS_MARKER_CSS_VARS = {
    Ruler: '--analysis-marker-ruler',
    '1x': '--analysis-marker-1x',
    BPFO: '--analysis-marker-bpfo',
    BPFI: '--analysis-marker-bpfi',
    BSF: '--analysis-marker-bsf',
    FTF: '--analysis-marker-ftf',
    '2xLF': '--analysis-marker-2xlf',
    LF: '--analysis-marker-lf',
    PPF: '--analysis-marker-ppf',
    RBF: '--analysis-marker-rbf',
    SSPF: '--analysis-marker-sspf',
} as const;

export enum AnalysisMarkerColors {
    Ruler = '#9298a6',
    '1x' = '#9c27b0',
    BPFO = '#fe5918',
    BPFI = '#0070c4',
    BSF = '#48a748',
    FTF = '#f29d01',
    '2xLF' = '#009ce1',
    LF = '#228910',
    PPF = '#d10d54',
    RBF = '#00bcd4',
    SSPF = '#ce4c00',
}

export function getAnalysisMarkerColorsFromCSS() {
    return resolveColorGroup(ANALYSIS_MARKER_CSS_VARS, AnalysisMarkerColors);
}

// --- Indicator Threshold Colors ---

export const INDICATOR_THRESHOLD_CSS_VARS = {
    off: '--indicator-threshold-off',
    alarm: '--indicator-threshold-alarm',
    warning: '--indicator-threshold-warning',
    criticalAlarm: '--indicator-threshold-critical-alarm',
} as const;

export enum IndicatorThresholdColors {
    off = '#a2a7b3', // grey-400
    alarm = '#ff1537',
    warning = '#f29d01',
    criticalAlarm = '#d2001b',
}

export function getIndicatorThresholdColorsFromCSS() {
    return resolveColorGroup(INDICATOR_THRESHOLD_CSS_VARS, IndicatorThresholdColors);
}

// --- Custom Marker Colors ---

export const CUSTOM_MARKER_CSS_VARS = {
    marker1: '--custom-marker-1',
    marker2: '--custom-marker-2',
    marker3: '--custom-marker-3',
    marker4: '--custom-marker-4',
    fault: '--custom-fault-marker',
} as const;

export enum CustomMarkerColors {
    marker1 = '#607d8b',
    marker2 = '#4caf50',
    marker3 = '#795548',
    marker4 = '#80d8ff',
    fault = '#2f3647', // grey-900
}

// Excludes fault — fault marker is accessed separately via CustomMarkerColors.fault
export const CustomMarkerColorsList = [
    CustomMarkerColors.marker1,
    CustomMarkerColors.marker2,
    CustomMarkerColors.marker3,
    CustomMarkerColors.marker4,
] as const;

// --- Chart Export Colors ---
// Colors used in SVG chart exports that render as <img> tags.
// CSS variables don't resolve in isolated <img> contexts, so these
// must always be hardcoded hex values.

export enum ChartExportColors {
    labelFontColor = '#ffffff', // foreground-inverse (white in both themes)
    bandColor = '#228910', // success-default / green-800
}

// ============================================
// Utility Functions
// ============================================

export function getAnalysisMarkerColor(
    markerName: keyof typeof AnalysisMarkerColors,
): string {
    const cssVar = ANALYSIS_MARKER_CSS_VARS[markerName];
    const fallback = AnalysisMarkerColors[markerName];
    return getCSSColor(cssVar, fallback);
}

export function getCustomMarkerColor(
    markerName: keyof typeof CustomMarkerColors,
): string {
    const cssVar = CUSTOM_MARKER_CSS_VARS[markerName];
    const fallback = CustomMarkerColors[markerName];
    return getCSSColor(cssVar, fallback);
}

export function getTrendLineColorForIndex(index: number): string {
    const cssVar = TREND_LINE_CSS_VARS[index % TREND_LINE_CSS_VARS.length];
    const fallback = TrendLineColorsList[index % TrendLineColorsList.length];
    return getCSSColor(cssVar, fallback);
}

export function getNextTrendLineColor(currentColors: string[]): string {
    const resolvedColors = getTrendLineColorsFromCSS();

    if (currentColors.length === 0) {
        return resolvedColors[0];
    }

    const colorCounts = resolvedColors.map((color, index) => ({
        color,
        index,
        count: countBy(currentColors, (current) => current === color),
    }));

    const sortedCounts = sortBy(
        colorCounts,
        (count) => count.count,
        (count) => count.index,
    );

    return sortedCounts[0].color;
}
